interface TranslateFunction {
  (key: string, params?: Record<string, any>): string;
  te?: (key: string) => boolean;
}

interface JsonApiError {
  code?: string;
  status?: string;
  title?: string;
  detail?: string;
  source?: {
    pointer?: string;
  };
  meta?: {
    attribute?: string;
    message?: string;
    code?: string;
    line_number?: number;
    details?: string;
  };
}

const CSV_DATA_PREFIX = /^Csv data\s+/i

export interface ParsedCsvValidationError {
  errorCode: string
  lineNumber: string
  rowData?: Record<string, string>
}

export function parseCsvValidationError(rawMessage: string): ParsedCsvValidationError | null {
  const message = rawMessage.replace(CSV_DATA_PREFIX, '').trim()
  const firstSeparator = message.indexOf(';')
  if (firstSeparator === -1) return null

  const errorCode = message.slice(0, firstSeparator).trim()
  const rest = message.slice(firstSeparator + 1)
  const secondSeparator = rest.indexOf(';')
  if (secondSeparator === -1) return null

  const lineNumber = rest.slice(0, secondSeparator).trim()
  const jsonDataStr = rest.slice(secondSeparator + 1)

  let rowData: Record<string, string> | undefined
  try {
    const cleanJsonStr = jsonDataStr.replace(/=>/g, ':')
    rowData = JSON.parse(cleanJsonStr)
  } catch {
    // ignore malformed row payload
  }

  return { errorCode, lineNumber, rowData }
}

function getCsvErrorTranslationKey(errorCode: string): string | null {
  const normalizedCode = errorCode.toLowerCase()

  const explicitKeys: Record<string, string> = {
    date_in_the_past: 'gifts.validation.errors.date_in_the_past',
    invalid_email_format: 'gifts.validation.errors.invalidEmail',
    multi_choice_card_not_exist: 'gifts.validation.errors.invalidGiftCard',
    invalid_amount: 'gifts.validation.errors.invalidAmount',
    invalid_date: 'gifts.validation.errors.invalidDate',
  }

  return explicitKeys[normalizedCode] || `gifts.validation.errors.${normalizedCode}`
}

function formatParsedCsvError(
  parsed: ParsedCsvValidationError,
  t: TranslateFunction,
  includeLineNumber: boolean
): string {
  const { errorCode, lineNumber, rowData } = parsed
  const prefix = includeLineNumber ? `${t('common.line')} ${lineNumber}: ` : ''

  if (errorCode.startsWith('MISSING_')) {
    const field = errorCode.replace('MISSING_', '').toLowerCase()
    return `${prefix}${t('gifts.validation.errors.fieldRequired', { field })}`
  }

  const translationKey = getCsvErrorTranslationKey(errorCode)
  if (translationKey && t.te?.(translationKey)) {
    return `${prefix}${t(translationKey)}`
  }

  return `${prefix}${rowData?.errors || errorCode}`
}

function extractCsvErrorMessage(error: JsonApiError): string | null {
  const candidates = [
    error.meta?.code,
    error.meta?.message,
    error.detail
  ]

  for (const candidate of candidates) {
    if (!candidate) continue
    const parsed = parseCsvValidationError(candidate)
    if (parsed) return candidate.replace(CSV_DATA_PREFIX, '').trim()
  }

  return null
}

export function formatErrorMessage(
  error: string | JsonApiError,
  t: TranslateFunction,
  options: { includeLineNumber?: boolean } = {}
): string {
  const includeLineNumber = options.includeLineNumber !== false

  if (typeof error === 'string') {
    const parsed = parseCsvValidationError(error)
    if (parsed) {
      return formatParsedCsvError(parsed, t, includeLineNumber)
    }
    return error
  }

  try {
    const rawMessage = extractCsvErrorMessage(error)
    if (!rawMessage) {
      return error.detail || error.title || String(t('gifts.validation.errors.unknown'))
    }

    const parsed = parseCsvValidationError(rawMessage)
    if (!parsed) {
      return error.detail || error.title || String(t('gifts.validation.errors.unknown'))
    }

    return formatParsedCsvError(parsed, t, includeLineNumber)
  } catch (parseError) {
    console.error('Erreur lors du parsing:', parseError)
    return error.detail || error.title || String(t('gifts.validation.errors.unknown'))
  }
}

export function formatApiErrorResponse(err: any, t: TranslateFunction): string {
  if (err.response?.status === 422) {
    const errorData = err.response.data
    if (Array.isArray(errorData.errors)) {
      return errorData.errors
        .map((error: any) => formatErrorMessage(error, t))
        .join('\n')
    }
    return errorData.error || errorData.message || String(t('gifts.validation.errors.validationWithCode'))
  }

  return err.response?.data?.error ||
         err.response?.data?.message ||
         err.message ||
         String(t('gifts.validation.errors.generic'))
}
